'use no memo';

/* eslint-disable complexity */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router';

import { Restaurant } from '@local/api';

import { isCateringOrderFlow, isYelpPlatform } from '@local/utils';

import { isAbortError } from 'helpers/api';
import useAssessFeatureFlag, { FLAG_NAME } from 'hooks/useAssessFeatureFlag';
import {
  getMarketingOptInStatusFromSessionStorage,
  getUserSessionId,
} from 'helpers/customer';
import {
  getOrder,
  normalizeOrderResponse,
  submitOrder as apiSubmitOrder,
  submitYelpOrder as apiSubmitYelpOrder,
  validateOrder as apiValidateOrder,
  validateYelpOrder as apiValidateYelpOrder,
} from 'helpers/orderApi';
import {
  COMPLETED_ORDER_STATUSES,
  FULFILLMENT_METHODS,
  isFulfillmentMethod,
} from 'helpers/order';
import { getIsTableRequired } from 'helpers/restaurantApi';
import { getRestaurantIdFromUrl } from 'helpers/url';

import type { CustomerVehicle, UserDataType } from 'types/customer';
import type { MenuDataType, OnAddItemType } from 'types/menu';
import type { FulfillMethod, OrderType, SubmittedOrderType } from 'types/order';

import buildSubmitOrderPayload, {
  SubmitOrderPayload,
} from './buildSubmitOrderPayload';
import buildValidatePayload from './buildValidatePayload';
import { loadOrderDataFromStorage, writeOrderDataToStorage } from './storage';

export const ORDER_STATE_LOADING = 'loading';
export const ORDER_STATE_VALIDATING = 'validating';
export const ORDER_STATE_LOADED = 'loaded';
export const ORDER_STATE_SUBMITTING = 'submitting';

export type OrderState =
  | typeof ORDER_STATE_LOADING
  | typeof ORDER_STATE_VALIDATING
  | typeof ORDER_STATE_LOADED
  | typeof ORDER_STATE_SUBMITTING;

export type UpdateOrderAction = (
  patch: LocalOrderType | undefined
) => Partial<LocalOrderType>;

export type LocalOrderType = {
  // these fields are local to Ordering
  pending_validation?: boolean;
  is_catering?: boolean;

  // these fields are from the API / root order type
  id?: string;
  guid?: string;
  area?: OrderType['area'];
  estimated_fulfillment_time?: OrderType['estimated_fulfillment_time'];
  fulfill_method: OrderType['fulfill_method'];
  include_single_use_items?: OrderType['include_single_use_items'];
  item_total?: OrderType['item_total'];
  items: OnAddItemType[];
  misc_fee?: OrderType['misc_fee'] | null;
  promo_code?: OrderType['promo_code'];
  restaurant_id: OrderType['restaurant_id'];
  status?: SubmittedOrderType['status'];
  table_name?: string | null;
  total?: OrderType['total'];
  total_due?: OrderType['total_due'];
  /** @deprecated */
  tip?: number;
  tips?: Partial<OrderType['tips']> | null;
  when: OrderType['when'];
  vehicle?: CustomerVehicle;
};

function notImplemented(): never {
  throw new Error('not implemented');
}

export const OrderContext = React.createContext<{
  errors: Array<{
    code: string;
    message: string;
  }>;
  order: LocalOrderType | undefined;
  refetchOrder: (
    controller?: AbortController
  ) => Promise<Awaited<ReturnType<typeof getOrder>>['data']>;
  resetOrder: () => void;
  setOnNextValidateError: (
    onNextValidateError: ((errorMessages: string[]) => void) | undefined
  ) => void;
  setOnNextValidateSuccess: (
    onNextValidateSuccess: VoidFunction | undefined
  ) => void;
  state: OrderState;
  startOrder: (
    fulfillmentMethod: FulfillMethod,
    when: string | undefined | null
  ) => void;
  submitOrder: (
    payload: SubmitOrderPayload | undefined,
    user: UserDataType,
    menu: MenuDataType,
    // $TSFIXME: type when typed in UserContext
    rewards: unknown[],
    rewardsOptIn: { optInStatus?: boolean } | undefined,
    controller?: AbortController
  ) => ReturnType<typeof apiSubmitOrder>;
  updateOrder: (
    payload: Partial<LocalOrderType> | UpdateOrderAction,
    triggerValidation?: boolean
  ) => void;
  validateOrder: (
    user: UserDataType,
    restaurant: Restaurant,
    menu: MenuDataType,
    fulfillmentEta?: { pickup?: string; delivery?: string },
    controller?: AbortController
  ) => ReturnType<typeof apiValidateOrder>;
}>({
  errors: [],
  order: undefined,
  refetchOrder: notImplemented,
  resetOrder: notImplemented,
  setOnNextValidateError: notImplemented,
  setOnNextValidateSuccess: notImplemented,
  state: 'loading',
  startOrder: notImplemented,
  submitOrder: notImplemented,
  updateOrder: notImplemented,
  validateOrder: notImplemented,
});

export type OrderContextType = React.ContextType<typeof OrderContext>;

export default function OrderProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const location = useLocation();
  const restaurantId = getRestaurantIdFromUrl(location.pathname);
  const userId = getUserSessionId();

  const marketplaceObnBypass = !!useAssessFeatureFlag(
    FLAG_NAME.flagMpObnChannelBypass
  );

  const orderStorageId = restaurantId
    ? `${userId}_${restaurantId}_${isCateringOrderFlow() ? 'catering' : 'order'}`
    : undefined;

  const [order, setOrder] = useState<LocalOrderType | undefined>(() =>
    orderStorageId ? loadOrderDataFromStorage(orderStorageId) : undefined
  );
  const [orderErrors, setOrderErrors] = useState<
    Array<{
      code: string;
      message: string;
    }>
  >([]);

  const [onNextValidateSuccess, setOnNextValidateSuccess] = useState<
    VoidFunction | undefined
  >(undefined);

  const [onNextValidateError, setOnNextValidateError] = useState<
    ((errorMessages: string[]) => void) | undefined
  >(undefined);

  const [state, setState] = useState<OrderState>(ORDER_STATE_LOADED);

  useEffect(() => {
    if (
      !orderStorageId ||
      // if the order is completed, skip saving it as we're going to clear it anyways
      (order?.status && COMPLETED_ORDER_STATUSES.includes(order.status))
    ) {
      return;
    }

    writeOrderDataToStorage(orderStorageId, order);
  }, [order, orderStorageId]);

  const startOrder = useCallback<OrderContextType['startOrder']>(
    (fulfillmentMethod, when) => {
      if (!restaurantId || !isFulfillmentMethod(fulfillmentMethod)) {
        return;
      }

      setOrder({
        items: [],
        restaurant_id: restaurantId,
        fulfill_method: fulfillmentMethod,
        when: when ?? null,
        pending_validation: fulfillmentMethod === FULFILLMENT_METHODS.delivery,
        is_catering: isCateringOrderFlow(),
      });
    },
    [restaurantId]
  );

  const resetOrder = useCallback<OrderContextType['resetOrder']>(() => {
    setOrder(undefined);

    /* we need to manually trigger this in here since by the time the effect which is otherwise in charge of syncing
     * to Session Storage above runs when switching from Checkout -> Confirmation, the page has already navigated
     * and the restaurant id has been lost from the URL */
    if (orderStorageId) {
      // really this fn. should not be called without having an orderStorageId
      writeOrderDataToStorage(orderStorageId, undefined);
    }
  }, [orderStorageId]);

  const updateOrder = useCallback<OrderContextType['updateOrder']>(
    (payload, triggerValidation = true) => {
      setOrder((prevOrder) => {
        let nextOrder = {
          ...prevOrder,
          // note this is intentionally above payload as we want callsites / actions
          // to be able to inline the pending_validation attribute as well
          pending_validation: !!triggerValidation,
          ...(typeof payload === 'function' ? payload(prevOrder) : payload),
        } as LocalOrderType;

        if (
          prevOrder?.items &&
          prevOrder.items.length > 0 &&
          nextOrder.items.length === 0
        ) {
          // reset tips when removing all items from an order
          nextOrder = {
            ...nextOrder,
            tips: {
              restaurant_tip: null,
              managed_delivery_tip: null,
            },
          };
        }

        return nextOrder;
      });
    },
    []
  );

  /**
   * you likely don't want to use this directly. use the exposed validateOrder
   * from ValidatedOrderProvider / ValidateOrderContext
   */
  const validateOrder = useCallback<OrderContextType['validateOrder']>(
    async (user, restaurant, menu, fulfillmentEta, controller) => {
      if (!order) {
        throw new Error('called validateOrder without an order');
      }

      setState(ORDER_STATE_VALIDATING);

      const payload = buildValidatePayload(
        {
          ...order,
          table_name: getIsTableRequired(restaurant)
            ? order.table_name
            : undefined,
          menu_id: menu.id!,
        },
        user,
        fulfillmentEta
      );

      const apiFn = isYelpPlatform() ? apiValidateYelpOrder : apiValidateOrder;

      const validateResponse = await apiFn(payload, {
        controller,
        flags: { marketplaceObnBypass },
      });

      if (isAbortError(validateResponse.error)) {
        setState(ORDER_STATE_LOADED);
        return validateResponse;
      }

      if (validateResponse.error) {
        const errorArray = Array.isArray(validateResponse.error)
          ? validateResponse.error
          : [validateResponse.error];
        setOrderErrors(errorArray);
        onNextValidateError?.(
          errorArray.map((error: { message: string }) => error.message)
        );
      } else {
        setOrderErrors([]);
        onNextValidateSuccess?.();
      }
      setOnNextValidateError(undefined);
      setOnNextValidateSuccess(undefined);

      updateOrder(
        validateResponse.data
          ? normalizeOrderResponse(validateResponse.data)
          : {},
        false
      );

      setState(ORDER_STATE_LOADED);

      return validateResponse;
    },
    [order, updateOrder, marketplaceObnBypass]
  );

  /**
   * you likely don't want to use this directly. use the exposed submitOrder
   * from useSubmitOrder
   */
  const submitOrder = useCallback<OrderContextType['submitOrder']>(
    async (payload, user, menu, rewards, rewardsOptIn, controller) => {
      if (!order) {
        throw new Error('submitOrder called without an order');
      }

      setState(ORDER_STATE_SUBMITTING);

      const apiFn = isYelpPlatform() ? apiSubmitYelpOrder : apiSubmitOrder;

      const marketingOptIn = getMarketingOptInStatusFromSessionStorage();

      const orderSubmitPayload = buildSubmitOrderPayload({
        payload,
        order: { ...order, menu_id: menu.id! },
        user,
        rewards,
        rewardsOptIn,
        marketingOptIn: marketingOptIn?.optInStatus,
      });

      const submitResponse = await apiFn(orderSubmitPayload, {
        controller,
        flags: { marketplaceObnBypass },
      });

      if (submitResponse.data) {
        updateOrder(
          {
            id: submitResponse.data.id,
            status: submitResponse.data.status,
            /**
             * the guid is on Apple Guest checkout orders, is required on the confirmation page,
             * and is used in useSubmitOrder to build the redirect URL to the confirmation page
             */
            guid: submitResponse.data.guid,
          },
          false
        );
      } else {
        setOrderErrors(
          Array.isArray(submitResponse.error)
            ? submitResponse.error
            : [submitResponse.error]
        );
      }

      setState(ORDER_STATE_LOADED);

      return submitResponse;
    },
    [order, marketplaceObnBypass]
  );

  const refetchOrder = useCallback<OrderContextType['refetchOrder']>(
    async (controller) => {
      if (!order?.id) {
        return undefined;
      }

      const { data } = await getOrder(order.id, undefined, { controller });

      if (data) {
        // note this _must_ go before updateOrder
        if (data.errors) {
          setOrderErrors(data.errors);
        }

        updateOrder(
          {
            id: data.id,
            status: data.status,
          },
          false
        );
      }

      return data;
    },
    [order, updateOrder]
  );

  const mValue = useMemo(
    () => ({
      errors: orderErrors,
      order,
      refetchOrder,
      resetOrder,
      setOnNextValidateSuccess,
      setOnNextValidateError,
      startOrder,
      state,
      submitOrder,
      updateOrder,
      validateOrder,
    }),
    [
      order,
      refetchOrder,
      resetOrder,
      setOnNextValidateSuccess,
      setOnNextValidateError,
      startOrder,
      state,
      submitOrder,
      updateOrder,
      orderErrors,
      validateOrder,
    ]
  );

  return (
    <OrderContext.Provider value={mValue}>{children}</OrderContext.Provider>
  );
}
